import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';
import { calculateNightShift, formatCurrency, formatNumber } from '../utils/timeCalculations';
import { getCurrentMonthYear } from '../utils/currentDate';

const AdicionalNoturnoPage = () => {
  const [valorHoraDiurna, setValorHoraDiurna] = useState('');
  const [percentualAdicional, setPercentualAdicional] = useState(20);
  const [horasNoturnas, setHorasNoturnas] = useState(0);

  const result = useMemo(() => {
    if (!valorHoraDiurna || parseFloat(valorHoraDiurna) <= 0 || horasNoturnas <= 0) return null;
    
    const valor = parseFloat(valorHoraDiurna);
    return calculateNightShift(valor, percentualAdicional, horasNoturnas);
  }, [valorHoraDiurna, percentualAdicional, horasNoturnas]);

  const faqs = [
    {
      question: 'O que é adicional noturno?',
      answer: 'O adicional noturno é um acréscimo de no mínimo 20% sobre o valor da hora diurna, pago a trabalhadores que exercem atividades entre 22h e 5h (trabalho urbano). Essa compensação existe porque o trabalho noturno é mais desgastante, afetando o sono, a saúde e o convívio social do trabalhador.'
    },
    {
      question: 'Quanto vale a hora noturna pela CLT?',
      answer: 'A hora noturna vale pelo menos 20% a mais que a hora diurna. Se sua hora diurna é R$ 10,00, a hora noturna será no mínimo R$ 12,00. Além disso, a hora noturna é reduzida para 52 minutos e 30 segundos, o que aumenta proporcionalmente o total de horas computadas e o valor recebido.'
    },
    {
      question: 'Quem tem direito ao adicional noturno?',
      answer: 'Todo trabalhador com carteira assinada (CLT) que trabalha no período noturno (22h às 5h para urbanos, 21h às 5h para rurais na lavoura, 20h às 4h na pecuária). Inclui trabalhadores com jornada mista, intermitentes e empregados domésticos. O adicional é proporcional às horas efetivamente trabalhadas no período noturno.'
    },
    {
      question: 'O que é hora noturna reduzida?',
      answer: 'A hora noturna reduzida é uma regra da CLT (Art. 73, §1) que define que cada hora do trabalho noturno equivale a 52 minutos e 30 segundos, não 60 minutos. Isso significa que 7 horas de relógio no período noturno são computadas como 8 horas de trabalho. O fator de conversão é 1,1428 (60 / 52,5).'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Calculadora de Adicional Noturno CLT — Hora Reduzida</title>
        <meta name="description" content="Calcule o adicional noturno conforme Art. 73 da CLT. Hora noturna com 20% de adicional e reduzida de 52 min e 30 s. Resultado em R$. Atualizada 2026." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large" />
        <link rel="canonical" href="https://calculadoradehoras.net.br/calculadora-de-adicional-noturno" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Calculadora de Adicional Noturno CLT — Hora Reduzida" />
        <meta property="og:description" content="Calcule o adicional noturno conforme Art. 73 da CLT. Hora noturna com 20% de adicional e reduzida de 52 min e 30 s. Resultado em R$. Atualizada 2026." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/calculadora-de-adicional-noturno" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Calculadora de Adicional Noturno CLT — Hora Reduzida" />
        <meta name="twitter:description" content="Calcule o adicional noturno conforme Art. 73 da CLT. Hora noturna com 20% de adicional e reduzida de 52 min e 30 s. Resultado em R$. Atualizada 2026." />
        
        <link rel="alternate" hrefLang="pt-BR" href="https://calculadoradehoras.net.br/calculadora-de-adicional-noturno" />
        <link rel="alternate" hrefLang="x-default" href="https://calculadoradehoras.net.br/calculadora-de-adicional-noturno" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Adicional Noturno","item":"https://calculadoradehoras.net.br/calculadora-de-adicional-noturno"}
        ]
      }} />

      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4">
          <a href="/" className="hover:text-blue-primary">Início</a>
          <span className="mx-2">›</span>
          <span>Adicional Noturno</span>
        </nav>
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-dark-text mb-4">
            Calculadora de Adicional Noturno CLT
          </h1>
          <p className="text-lg text-body-text">
            Calcule o adicional noturno com hora reduzida conforme o Art. 73 da CLT
          </p>
          <p className="text-sm text-body-text mt-2" data-testid="last-updated" suppressHydrationWarning>{`Última atualização: ${getCurrentMonthYear()}`}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Valor da Hora Diurna (R$)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={valorHoraDiurna}
                onChange={(e) => setValorHoraDiurna(e.target.value)}
                placeholder="0.00"
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                data-testid="valor-hora-diurna"
              />
              <p className="mt-1 text-sm text-body-text">
                Divida seu salário mensal pelo divisor de horas (geralmente 220h)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Percentual do Adicional Noturno (%)
              </label>
              <input
                type="number"
                min="20"
                max="100"
                value={percentualAdicional}
                onChange={(e) => setPercentualAdicional(parseInt(e.target.value) || 20)}
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                data-testid="percentual-adicional"
              />
              <p className="mt-1 text-sm text-body-text">
                Mínimo legal: 20% (Art. 73 CLT). Convenções coletivas podem prever mais
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Horas Trabalhadas no Período Noturno (22h-5h)
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={horasNoturnas}
                onChange={(e) => setHorasNoturnas(parseFloat(e.target.value) || 0)}
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                data-testid="horas-noturnas"
              />
            </div>
          </div>

          {result && (
            <div className="mt-8 space-y-4">
              <div className="bg-light-bg rounded-lg p-6">
                <h3 className="font-heading font-semibold text-dark-text mb-4">Detalhamento do Cálculo</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-body-text">Valor hora diurna:</span>
                    <span className="font-mono font-bold">{formatCurrency(parseFloat(valorHoraDiurna))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-body-text">Adicional noturno ({percentualAdicional}%):</span>
                    <span className="font-mono font-bold text-blue-primary">+{formatCurrency(parseFloat(result.adicionalReais))}</span>
                  </div>
                  <div className="flex justify-between border-t pt-3">
                    <span className="text-body-text">Valor hora noturna:</span>
                    <span className="font-mono font-bold text-lg">{formatCurrency(parseFloat(result.valorHoraNoturna))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-body-text">Horas informadas (relógio):</span>
                    <span className="font-mono">{horasNoturnas}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-body-text">Horas reduzidas (computadas):</span>
                    <span className="font-mono font-bold text-blue-primary">{formatNumber(result.horasReduzidas)}h</span>
                  </div>
                  <div className="text-xs text-body-text mt-2">
                    * Fator de redução: {result.fatorReducao} (hora noturna = 52min30s)
                  </div>
                </div>
              </div>

              <div className="bg-blue-primary rounded-xl p-6 text-white">
                <h3 className="text-lg mb-4 opacity-90">Resultado Final</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-2xl">
                    <span>Total a Receber:</span>
                    <span className="font-mono font-bold">{formatCurrency(parseFloat(result.totalReceber))}</span>
                  </div>
                  <div className="border-t border-white/20 pt-3 space-y-2 text-sm opacity-90">
                    <div className="flex justify-between">
                      <span>Se fosse hora diurna normal:</span>
                      <span className="font-mono">{formatCurrency(parseFloat(result.semAdicional))}</span>
                    </div>
                    <div className="flex justify-between text-base font-bold text-yellow-300">
                      <span>Benefício da legislação:</span>
                      <span className="font-mono">+{formatCurrency(parseFloat(result.beneficio))}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-white/20">
                  <button
                    onClick={() => {
                      const message = `Adicional Noturno: ${horasNoturnas}h trabalhadas = ${formatNumber(result.horasReduzidas)}h computadas | Valor: ${formatCurrency(parseFloat(result.totalReceber))} | Calculado em https://calculadoradehoras.net.br/calculadora-de-adicional-noturno`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
                    }}
                    className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                    style={{ backgroundColor: '#25D366' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1fb855'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#25D366'}
                    data-testid="whatsapp-share-button"
                  >
                    <svg 
                      className="mr-2" 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="currentColor"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Compartilhar via WhatsApp
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CTA to Guide Page */}
        <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-2xl p-6 md:p-8 mb-8 border-2 border-indigo-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <BookOpen className="text-indigo-600" size={32} />
                <p className="font-bold text-xl font-semibold text-dark-text">
                  Quer aprender mais sobre adicional noturno?
                </p>
              </div>
              <p className="text-base text-body-text">
                Acesse nosso <strong>guia completo com tabelas de valores, regras da CLT</strong> e exemplos práticos para entender como funciona o adicional noturno.
              </p>
            </div>
            <Link
              to="/adicional-noturno-valor"
              className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Ver Guia Completo
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>

        <AdSlot slotId="ad-slot-1" />

        <article className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Como Calcular o Adicional Noturno</h2>
            <p className="text-body-text leading-relaxed mb-4">
              O adicional noturno é um direito garantido pela CLT a todo trabalhador que exerce suas atividades no período da noite. Nossa calculadora aplica automaticamente o percentual de 20% sobre a hora diurna e a hora noturna reduzida de 52 minutos e 30 segundos.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">O Que Diz o Art. 73 da CLT</h2>
            <p className="text-body-text leading-relaxed mb-4">
              O <a href="https://www.planalto.gov.br/ccivil_03/decreto-lei/del5452.htm#art73" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Art. 73 da CLT</a> estabelece três regras fundamentais:
            </p>
            <ol className="list-decimal list-inside space-y-3 text-body-text mb-4">
              <li><strong>Adicional de 20%:</strong> O trabalho noturno deve ter remuneração superior ao diurno, com acréscimo de pelo menos 20% sobre a hora diurna.</li>
              <li><strong>Hora noturna reduzida (§1):</strong> A hora do trabalho noturno é computada como 52 minutos e 30 segundos, não 60 minutos.</li>
              <li><strong>Período noturno (§2):</strong> Para trabalhadores urbanos, o trabalho noturno é aquele executado entre as 22 horas de um dia e as 5 horas do dia seguinte.</li>
            </ol>
            <p className="text-body-text leading-relaxed">
              Para mais informações sobre seus direitos trabalhistas relacionados ao adicional noturno, consulte o site do <a href="https://www.tst.jus.br/" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Tribunal Superior do Trabalho (TST)</a>.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Como Funciona a Hora Noturna Reduzida</h2>
            <p className="text-body-text leading-relaxed mb-4">
              A hora noturna reduzida é uma criação jurídica que reconhece o maior desgaste do trabalho noturno. Para converter horas do relógio em horas noturnas computadas, usa-se o fator de conversão <strong>1,1428</strong> (60 minutos dividido por 52,5 minutos).
            </p>
            <div className="bg-light-bg p-6 rounded-lg">
              <p className="text-body-text leading-relaxed">
                <strong>Exemplo prático:</strong> Um trabalhador que trabalha das 22h às 5h cumpre 7 horas de relógio. Ao aplicar o fator: 7h × (60 ÷ 52,5) = <strong>8 horas noturnas computadas</strong>. Isso significa que, para fins de pagamento, ele é remunerado por 8 horas, não 7.
              </p>
            </div>
          </section>
        </article>

        <AdSlot slotId="ad-slot-2" />

        <section className="my-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-6">Perguntas Frequentes</h2>
          <FAQAccordion faqs={faqs} />
        </section>

        <RelatedTools currentPath="/calculadora-de-adicional-noturno" />

        <AdSlot slotId="ad-slot-3" />
      </main>

      <Footer />
    </>
  );
};

export default AdicionalNoturnoPage;
